/**
 * Token amount formatting.
 *
 * Every amount in this app is a `bigint` of raw units. `number` cannot hold
 * 9,999,999e18 without losing precision, so it never touches a balance — these
 * helpers convert straight from bigint to string for display and parse strings
 * back to bigint for input. There is no `parseFloat` anywhere in the path.
 */
import { TOKEN_DECIMALS } from "./config";

/** Raw units -> plain decimal string, e.g. 1250500000000000000000 -> "1250.5" */
export function formatRaw(raw: bigint, decimals: number = TOKEN_DECIMALS): string {
  const negative = raw < 0n;
  const abs = negative ? -raw : raw;
  const base = 10n ** BigInt(decimals);
  const whole = abs / base;
  const frac = (abs % base).toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

/** Raw units -> grouped display string, e.g. "1.250,5" in id-ID. */
export function formatDisplay(
  raw: bigint,
  decimals: number = TOKEN_DECIMALS,
  maxFractionDigits = 6,
): string {
  const plain = formatRaw(raw, decimals);
  const [whole, frac = ""] = plain.split(".");
  const grouped = BigInt(whole).toLocaleString("id-ID");
  const trimmed = frac.slice(0, maxFractionDigits).replace(/0+$/, "");
  return trimmed ? `${grouped},${trimmed}` : grouped;
}

export class AmountParseError extends Error {}

/**
 * Decimal string -> raw units, without floating point.
 *
 * Rejects rather than rounds when the user supplies more decimal places than
 * the token has: silently truncating someone's amount is not acceptable in a
 * flow that is irreversible.
 */
export function parseAmount(input: string, decimals: number = TOKEN_DECIMALS): bigint {
  const trimmed = input.trim().replace(/,/g, ".");
  if (trimmed === "") throw new AmountParseError("Jumlah tidak boleh kosong");
  if (!/^\d*\.?\d*$/.test(trimmed)) {
    throw new AmountParseError("Jumlah hanya boleh berisi angka");
  }

  const [whole = "0", frac = ""] = trimmed.split(".");
  if (frac.length > decimals) {
    throw new AmountParseError(`Maksimal ${decimals} angka di belakang koma`);
  }

  const raw = BigInt(whole || "0") * 10n ** BigInt(decimals) + BigInt(frac.padEnd(decimals, "0") || "0");
  if (raw <= 0n) throw new AmountParseError("Jumlah harus lebih besar dari nol");
  return raw;
}

/** 0x1234…abcd */
export function shortenAddress(address: string, lead = 6, tail = 4): string {
  if (address.length <= lead + tail) return address;
  return `${address.slice(0, lead)}…${address.slice(-tail)}`;
}

/** Lock length in human terms, e.g. "90 hari" or "1 tahun 6 bulan". */
export function formatDuration(seconds: bigint | number): string {
  const total = Number(seconds);
  const days = Math.round(total / 86_400);

  if (days < 1) {
    const hours = Math.round(total / 3_600);
    return hours < 1 ? `${Math.round(total / 60)} menit` : `${hours} jam`;
  }
  if (days < 60) return `${days} hari`;
  if (days < 365) return `${Math.round(days / 30)} bulan`;

  const years = Math.floor(days / 365);
  const months = Math.round((days % 365) / 30);
  return months > 0 ? `${years} tahun ${months} bulan` : `${years} tahun`;
}

/** Time remaining until `target`, measured against the chain's clock. */
export function formatCountdown(target: bigint, now: bigint): string {
  if (now >= target) return "sudah jatuh tempo";

  const remaining = Number(target - now);
  const days = Math.floor(remaining / 86_400);
  const hours = Math.floor((remaining % 86_400) / 3_600);
  const minutes = Math.floor((remaining % 3_600) / 60);

  if (days > 0) return `${days} hari ${hours} jam lagi`;
  if (hours > 0) return `${hours} jam ${minutes} menit lagi`;
  return `${minutes} menit lagi`;
}

export function formatTimestamp(seconds: bigint | number): string {
  const date = new Date(Number(seconds) * 1000);
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "long",
    timeStyle: "short",
    timeZone: "Asia/Jakarta",
  }).format(date);
}
